import { createAction, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { downloadBill, getBillHistory } from 'helpers/api';
import { IBillHistory, IBillsState, TFetchMoreThunkAction, TPagingParams } from 'modules/MyBills';
import { AppDispatch } from 'stores';
import { AxiosError } from 'axios';
import { ErrorResponse } from 'helpers/type';

export const initialState: IBillsState = {
    isError: false,
    isLoading: true,
    latest: null,
    history: {
        data: [],
        meta: {
            totalRecords: 0,
            page: 0,
            pageSize: 10,
        },
        isLoading: false,
    },
    error: null,
    isDownloading: false,
};

const FETCH_BILL_HISTORY_REQUESTED = 'MY_BILLS__FETCH_BILL_HISTORY_REQUESTED';
const FETCH_BILL_HISTORY_SUCCEEDED = 'MY_BILLS__FETCH_BILL_HISTORY_SUCCEEDED';
const FETCH_BILL_HISTORY_FAILED = 'MY_BILLS__FETCH_BILL_HISTORY_FAILED';

export const fetchRequested = createAction(FETCH_BILL_HISTORY_REQUESTED);
export const fetchSucceeded = createAction<IBillHistory>(FETCH_BILL_HISTORY_SUCCEEDED);
export const fetchFailed = createAction<ErrorResponse>(FETCH_BILL_HISTORY_FAILED);

export const fetchMoreRequested = createAction('FETCH_MORE_BILL_HISTORY_REQUESTED');
export const fetchMoreSucceeded = createAction<IBillHistory>('FETCH_MORE_BILL_HISTORY_SUCCEEDED');
export const fetchMoreFailed = createAction<ErrorResponse>('FETCH_MORE_BILL_HISTORY_FAILED');

export const fetchBills = (page: TPagingParams) => {
    return (dispatch: AppDispatch) => {
        dispatch(fetchRequested());
        getBillHistory(page)
            .then(res => {
                dispatch(fetchSucceeded(res.data));
            })
            .catch(err => {
                dispatch(fetchFailed(err));
            });
    };
};

export const fetchMoreBills = (page: TPagingParams): TFetchMoreThunkAction => {
    return dispatch => {
        dispatch(fetchMoreRequested());
        return getBillHistory(page)
            .then(res => {
                dispatch(fetchMoreSucceeded(res.data));
            })
            .catch(err => {
                dispatch(fetchMoreFailed(err));
            });
    };
};

export const downloadMyBillsPdf = createAsyncThunk<void, string, { rejectValue: AxiosError }>(
    'BILL_DOWNLOAD_PDF',
    async invoiceId => {
        return await downloadBill(invoiceId);
    },
);

const slice = createSlice({
    name: 'bills',
    initialState,
    reducers: {},
    extraReducers: {
        [fetchRequested.type](state: IBillsState) {
            state.isLoading = true;
            state.isError = false;
            state.error = null;
        },
        [fetchSucceeded.type](state: IBillsState, action: PayloadAction<IBillHistory>) {
            const { data, meta } = action.payload;
            state.isLoading = false;
            state.isError = false;
            if (data.length > 0) {
                const [latest, ...history] = data;
                state.latest = latest;
                state.history.data = history;
                state.history.meta = meta;
                state.history.isLoading = false;
            }
            return state;
        },
        [fetchFailed.type](state: IBillsState, action: PayloadAction<AxiosError>) {
            state.isLoading = false;
            state.isError = true;
            state.error = {
                status: action.payload?.response?.status,
                message: action.payload?.response?.data.message,
            };
            state.history.isLoading = false;
        },
        [fetchMoreRequested.type](state: IBillsState) {
            state.history.isLoading = true;
        },
        [fetchMoreSucceeded.type](state: IBillsState, action: PayloadAction<IBillHistory>) {
            const { data, meta } = action.payload;
            state.isLoading = false;
            state.isError = false;
            if (data.length > 0) {
                state.history.data = state.history.data.concat(data);
                state.history.meta = meta;
                state.history.isLoading = false;
            }
            return state;
        },
        [fetchMoreFailed.type](state: IBillsState, action: PayloadAction<AxiosError>) {
            state.isLoading = false;
            state.isError = true;
            state.error = {
                status: action.payload?.response?.status,
                message: action.payload?.response?.data.message,
            };
            state.history.isLoading = false;
        },
        [downloadMyBillsPdf.pending.type](state) {
            state.isDownloading = true;
        },
        [downloadMyBillsPdf.rejected.type](state) {
            state.isDownloading = false;
        },
        [downloadMyBillsPdf.fulfilled.type](state) {
            state.isDownloading = false;
        },
    },
});

export default slice.reducer;
